import { Container, Card, InfoTooltip } from "@/shared/ui";
import { useTableUnsubscribe } from "@/modules/ServiceCore/hooks/useTableUnsubscribe";
import { useToastStore } from "@/shared/stores/toastStore";
import { type UserSubscription } from "@/shared/api/types";
import styles from "./Subscriptions.module.scss";
import { useServiceWorkflowStore } from "@/shared/stores/serviceWorkflowStore";
import { useSubscriptions } from "@/shared/hooks/useSubscriptions";
import { useNavigate } from "@tanstack/react-router";

function getServiceName(tableType: "auto" | "jam"): string {
  return tableType === "auto" ? "АвтоТаблица" : "АвтоДжем";
}

function getServiceId(tableType: "auto" | "jam"): "autotables" | "autojam" {
  return tableType === "auto" ? "autotables" : "autojam";
}

export function Subscriptions() {
  const { subscriptions, isLoading, error, refetch } = useSubscriptions();
  const { showToast } = useToastStore();
  const navigate = useNavigate();

  const { setWorkflowState } = useServiceWorkflowStore();
  const { handleUnsubscribeRequest } = useTableUnsubscribe({
    showToast,
    onUnsubscribeSuccess: () => {
      refetch();
    },
  });

  const handleUnsubscribeClick = (sub: UserSubscription) => {
    setWorkflowState({
      serviceType: sub.table_type === "auto" ? "auto_table" : "auto_jam",
    });
    handleUnsubscribeRequest(sub.id, sub.table_type);
  };

  const handleBuyClick = (sub: UserSubscription) => {
    navigate({
      to: "/service/$id",
      params: { id: getServiceId(sub.table_type) },
      search: { pId: sub.supplier.id, stage: "payment", tableId: sub.id },
    });
  };

  const formatDate = (isoDate: string) => {
    if (!isoDate) return "—";
    return new Date(isoDate).toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className={styles.page}>
      <Container>
        {isLoading ? (
          <div className={styles.loadingOverlay}>
            <div className={styles.spinner}></div>
            <p className={styles.loadingText}>Загрузка подписок...</p>
          </div>
        ) : error ? (
          <div className={styles.error}>Не удалось загрузить подписки</div>
        ) : (
          <div className={styles.subscriptionsList}>
            {subscriptions.length > 0 ? (
              subscriptions.map((sub) => (
                <Card
                  key={sub.id}
                  className={styles.subscriptionCard}
                  variant="narrow"
                >
                  <div className={styles.cardHeader}>
                    <div>
                      <div className={styles.serviceName}>
                        {getServiceName(sub.table_type)}
                      </div>
                      <div className={styles.supplierName}>
                        {sub.supplier.name}
                      </div>
                    </div>
                  </div>
                  <div className={styles.cardBody}>
                    <div className={styles.infoBlock}>
                      <span className={styles.infoLabel}>Статус</span>
                      <span
                        className={`${styles.infoValue} ${sub.subscription_type !== "disabled" ? styles.statusActive : styles.statusInactive}`}
                      >
                        {sub.subscription_type !== "disabled"
                          ? "Активна"
                          : "Неактивна"}
                      </span>
                    </div>
                    {sub.table_type === "auto" && (
                      <div className={styles.infoBlock}>
                        <span className={styles.infoLabel}>Тип</span>
                        <span className={styles.infoValue}>
                          {sub.subscription_type === "free" ? (
                            <>
                              Бесплатная
                              <InfoTooltip text="Сейчас у вас бесплатная таблица за Р+. Оплатите сервис, чтобы получить полный функционал." />
                            </>
                          ) : sub.subscription_type === "paid" ? (
                            "Платная"
                          ) : (
                            "—"
                          )}
                        </span>
                      </div>
                    )}
                    <div className={styles.infoBlock}>
                      <span className={styles.infoLabel}>Дата окончания</span>
                      <span className={styles.infoValue}>
                        {sub.subscription_type === "free"
                          ? "—"
                          : formatDate(sub.expires_at)}
                      </span>
                    </div>
                  </div>
                  <div className={styles.cardFooter}>
                    {sub.subscription_type === "free" &&
                      sub.table_type === "auto" && (
                        <button
                          className={`${styles.actionButton} ${styles.buyButton}`}
                          onClick={() => handleBuyClick(sub)}
                        >
                          Купить подписку
                        </button>
                      )}
                    {sub.subscription_type !== "disabled" && (
                      <button
                        className={`${styles.actionButton} ${styles.unsubscribeButton}`}
                        onClick={() => handleUnsubscribeClick(sub)}
                      >
                        Отписаться
                      </button>
                    )}
                  </div>
                </Card>
              ))
            ) : (
              <div className={styles.empty}>У вас нет активных подписок.</div>
            )}
          </div>
        )}
      </Container>
    </div>
  );
}
